<script>
import FilterPanel from '@/components/common/FilterPanel.vue';
import FilterField from '@/components/common/FilterField.vue';
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue';
import DateRangeFilter from '@/components/common/DateRangeFilter.vue';
import SavedFilters from '@/components/common/SavedFilters.vue';
import { SAVED_FILTER_SCOPES } from '@/shared/services/saved-filter.service';

/**
 * Les critères de la liste des enquêtes.
 *
 * Un seul statut, un gouvernorat approchant et une date: la barre ne savait pas
 * répondre à « les brouillons et les soumises de tel gouvernorat pour
 * l'exercice 2025 », ni à « ce qu'a saisi tel enquêteur le mois dernier ». Les
 * choix multiples sont peuplés par l'API, et la date d'entretien ne se confond
 * plus avec la date de création: ce sont deux intervalles distincts.
 */
export default {
  name: 'SurveyFilters',
  components: {
    FilterPanel,
    FilterField,
    MultiSelectFilter,
    DateRangeFilter,
    SavedFilters,
  },
  props: {
    modelValue: { type: Object, required: true },
    /** { governorates, sectors, referenceYears, authors } */
    options: {
      type: Object,
      default: () => ({
        governorates: [],
        sectors: [],
        referenceYears: [],
        authors: [],
      }),
    },
    /** L'écran professionnel ne filtre pas par auteur: tout est de lui. */
    showAuthors: { type: Boolean, default: true },
    activeCount: { type: Number, default: 0 },
    criteria: { type: Object, default: () => ({}) },
    appliedFilterId: { type: String, default: null },
    expanded: { type: Boolean, default: false },
    scope: { type: String, default: SAVED_FILTER_SCOPES.SURVEYS },
  },
  emits: [
    'update:modelValue',
    'update:expanded',
    'reset',
    'apply-saved',
    'saved-filters-loaded',
  ],
  data() {
    return {
      statusOptions: [
        { value: 'SUBMITTED', label: 'Soumise' },
        { value: 'DRAFT', label: 'Brouillon' },
      ],
      respondentOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Renseigné' },
        { value: 'no', label: 'Non renseigné' },
      ],
    };
  },
  computed: {
    governorateChoices() {
      return (this.options.governorates || []).map((value) => ({
        value,
        label: value,
      }));
    },

    sectorChoices() {
      return (this.options.sectors || []).map((value) => ({
        value,
        label: value,
      }));
    },

    yearChoices() {
      return (this.options.referenceYears || []).map((year) => ({
        value: year,
        label: String(year),
      }));
    },

    authorChoices() {
      return (this.options.authors || []).map((author) => ({
        value: author.id,
        label: author.name,
      }));
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
  },
};
</script>

<template>
  <FilterPanel
    :model-value="modelValue.searchQuery"
    :expanded="expanded"
    :active-count="activeCount"
    search-placeholder="Rechercher une entreprise, un répondant…"
    @update:model-value="update('searchQuery', $event)"
    @update:expanded="$emit('update:expanded', $event)"
    @reset="$emit('reset')"
  >
    <template #quick>
      <select
        class="list-select"
        :value="modelValue.sort"
        aria-label="Trier les enquêtes"
        data-testid="surveys-sort"
        @change="update('sort', $event.target.value)"
      >
        <option value="recent">Tri : plus récentes</option>
        <option value="oldest">Tri : plus anciennes</option>
        <option value="status">Tri : statut</option>
      </select>
    </template>

    <template #saved>
      <SavedFilters
        :scope="scope"
        :criteria="criteria"
        :applied-id="appliedFilterId"
        @apply="$emit('apply-saved', $event)"
        @loaded="$emit('saved-filters-loaded', $event)"
      />
    </template>

    <template #advanced>
      <FilterField label="Statuts">
        <MultiSelectFilter
          :model-value="modelValue.statuses"
          :options="statusOptions"
          placeholder="Tous les statuts"
          @update:model-value="update('statuses', $event)"
        />
      </FilterField>

      <FilterField label="Gouvernorats">
        <MultiSelectFilter
          :model-value="modelValue.governorates"
          :options="governorateChoices"
          placeholder="Tous les gouvernorats"
          @update:model-value="update('governorates', $event)"
        />
      </FilterField>

      <FilterField label="Secteurs d'activité">
        <MultiSelectFilter
          :model-value="modelValue.sectors"
          :options="sectorChoices"
          placeholder="Tous les secteurs"
          @update:model-value="update('sectors', $event)"
        />
      </FilterField>

      <FilterField label="Exercices">
        <MultiSelectFilter
          :model-value="modelValue.referenceYears"
          :options="yearChoices"
          placeholder="Tous les exercices"
          @update:model-value="update('referenceYears', $event)"
        />
      </FilterField>

      <FilterField v-if="showAuthors" label="Saisies par">
        <MultiSelectFilter
          :model-value="modelValue.createdByIds"
          :options="authorChoices"
          placeholder="Tous les comptes"
          @update:model-value="update('createdByIds', $event)"
        />
      </FilterField>

      <FilterField label="Nom du répondant">
        <select
          class="list-select"
          :value="modelValue.hasRespondent"
          data-testid="surveys-has-respondent"
          @change="update('hasRespondent', $event.target.value)"
        >
          <option
            v-for="option in respondentOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Date d'entretien" wide>
        <DateRangeFilter
          :model-value="modelValue.interviewDate"
          @update:model-value="update('interviewDate', $event)"
        />
      </FilterField>

      <FilterField label="Date de création" wide>
        <DateRangeFilter
          :model-value="modelValue.dateAdded"
          @update:model-value="update('dateAdded', $event)"
        />
      </FilterField>
    </template>
  </FilterPanel>
</template>
